import { useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

import { customIcons as icons } from "~/ui";
import { tw } from "~/utils";

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectDropdownInputProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel?: string;
  disabled?: boolean;
  labelClassName?: string;
  optionsClassName?: string;
}

// Sentinel value for the pinned "All" row — never a real option id, so it
// can never collide with an actual option and is never persisted into
// `selected` (see handleChange below). Ported from the previous
// MultiSelectDropdown (bordered Combobox variant) this component replaces.
const ALL_ID = "__all__";

// Styled identically to DropdownInput (the app-wide list-screen filter
// convention: plain-text trigger + chevron, right-aligned panel, per-row
// checkbox icons, no bordered search box) rather than the old searchable
// Combobox look. The only genuine multiselect additions are the closed-state
// summary text, per-row (not single) checkmarks, and the pinned "All"
// select-all/deselect-all row — all ported verbatim from MultiSelectDropdown.
export const MultiSelectDropdownInput = ({
  options,
  selected,
  onChange,
  allLabel = "All",
  disabled = false,
  labelClassName,
  optionsClassName,
}: MultiSelectDropdownInputProps) => {
  const [optionsListed, setOptionsListed] = useState(false);

  // "All" is checked if and only if every specific option is selected —
  // classic tri-state select-all (not indeterminate: deselecting any single
  // option immediately unchecks "All" too, since this recomputes on every
  // render from the actual selection).
  const allSelected = options.length > 0 && selected.length === options.length;

  // The summary must always agree with what's actually checked. A genuinely
  // empty selection is NOT the same as "All" — it gets its own distinct
  // label instead.
  const summary = allSelected
    ? allLabel
    : selected.length === 0
      ? "None selected"
      : selected.length === 1
        ? (options.find((option) => option.id === selected[0])?.label ??
          allLabel)
        : `${selected.length.toString()} selected`;

  // Clicking "All" selects every option when not everything is already
  // selected, or clears everything when it is — a real select-all/deselect-
  // all toggle, not a reset. Selecting/deselecting a specific option never
  // touches this: "All" is purely derived (allSelected above).
  //
  // HeadlessUI's multiple-select toggles ALL_ID in/out of the bound array
  // like any other option value, so intercept it here rather than let it
  // accumulate alongside real selections — ALL_ID itself is never persisted.
  const handleChange = (values: string[]) => {
    if (values.includes(ALL_ID)) {
      onChange(allSelected ? [] : options.map((option) => option.id));
      return;
    }

    onChange(values);
  };

  return (
    <Listbox
      value={selected}
      onChange={handleChange}
      multiple
      disabled={disabled}
    >
      <div className="relative">
        <div className="outline-hidden inline-flex rounded-md">
          <ListboxButton
            className="data-open:border-b-violet outline-hidden data-open:visible inline-flex items-center border-b border-b-transparent px-3 py-1.5"
            onClick={() => setOptionsListed((prev) => !prev)}
          >
            <div className="text-indigo inline-flex items-center gap-x-1.5 rounded-l-md pr-3">
              <p className={tw("font-sans text-lg", labelClassName)}>
                {summary}
              </p>
            </div>

            {optionsListed ? (
              <ChevronUpIcon
                aria-hidden="true"
                className="text-midnight-gray size-5 forced-colors:text-[Highlight]"
              />
            ) : (
              <ChevronDownIcon
                aria-hidden="true"
                className="text-midnight-gray size-5 forced-colors:text-[Highlight]"
              />
            )}
          </ListboxButton>
        </div>

        <ListboxOptions
          transition
          className={tw(
            "border-light-lavender-gray focus:outline-hidden data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 absolute right-0 top-10 z-20 mt-4 max-h-72 w-52 origin-top-right overflow-y-auto rounded-lg border bg-white shadow-sm ring-1 ring-black/5",
            optionsClassName,
          )}
        >
          {/* "All" is pinned above the option list and always visible — it's
              a select-all affordance, not a filterable event type. */}
          <ListboxOption
            value={ALL_ID}
            className="text-indigo group cursor-default select-none p-3 text-[16px]"
          >
            <div className="flex flex-col">
              <div className="flex gap-3">
                {allSelected ? (
                  <icons.CheckedCheckbox />
                ) : (
                  <icons.UncheckedCheckbox />
                )}

                <p>{allLabel}</p>
              </div>
            </div>
          </ListboxOption>

          {options.map((option) => (
            <ListboxOption
              key={option.id}
              value={option.id}
              className="text-indigo group cursor-default select-none p-3 text-[16px]"
            >
              <div className="flex flex-col">
                <div className="flex gap-3">
                  {selected.includes(option.id) ? (
                    <icons.CheckedCheckbox />
                  ) : (
                    <icons.UncheckedCheckbox />
                  )}

                  <p>{option.label}</p>
                </div>
              </div>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
